import { Component, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProductService, ProductDTO, ProductFilterSpec } from '../../services/product.service';
import { ProductReadComponent } from './product-read.component';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { DrawerModule } from 'primeng/drawer';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { EntityState } from '../../core/models';

@Component({
  selector: 'app-products-list',
  templateUrl: './products-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .products-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        color: rgb(100 116 139);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.55rem 0.5rem !important;
        transition: background-color 0.15s ease, color 0.15s ease;
      }
      :host ::ng-deep .products-primeng-table .p-datatable-thead > tr > th.p-sortable-column {
        cursor: pointer;
        user-select: none;
      }
      :host ::ng-deep .products-primeng-table .p-datatable-thead > tr > th.p-sortable-column:hover {
        background: rgb(241 245 249);
        color: rgb(15 23 42);
      }
      :host ::ng-deep .products-primeng-table .p-datatable-thead > tr > th.p-sortable-column:focus-visible {
        outline: 2px solid rgb(14 165 233);
        outline-offset: -2px;
      }
      :host ::ng-deep .products-primeng-table .p-datatable-thead > tr > th.p-sortable-column.p-datatable-column-sorted {
        background: rgb(240 249 255);
        color: rgb(3 105 161);
      }
      :host ::ng-deep .products-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-datatable-sort-icon,
      :host ::ng-deep .products-primeng-table .p-datatable-thead > tr > th.p-sortable-column .p-sortable-column-icon,
      :host ::ng-deep .products-primeng-table .p-datatable-thead > tr > th.p-sortable-column p-sorticon svg {
        width: 0.75rem;
        height: 0.75rem;
        color: rgb(14 165 233);
        opacity: 1;
        flex-shrink: 0;
        margin-left: auto;
      }
      :host ::ng-deep .products-primeng-table .p-datatable-tbody > tr > td {
        border-color: rgb(241 245 249);
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .products-primeng-table .p-datatable-table {
        width: 100%;
        table-layout: auto;
      }
      :host ::ng-deep .products-primeng-table .p-datatable-tbody > tr:hover {
        background: rgb(240 249 255 / 0.5);
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .products-primeng-table.p-datatable,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container,
      :host ::ng-deep .p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .p-datatable-paginator-bottom {
        flex-shrink: 0;
      }
      :host ::ng-deep .p-paginator .p-paginator-page.p-paginator-page-selected,
      :host ::ng-deep button.p-paginator-page-selected {
        background: rgb(224 242 254) !important; /* sky-100 */
        border-color: rgb(14 165 233) !important; /* sky-500 */
        color: rgb(2 132 199) !important; /* sky-600 */
      }
      :host ::ng-deep .product-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .product-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    RouterLink,
    MatIconModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    FloatLabelModule,
    SelectModule,
    DrawerModule,
    ProductReadComponent,
    MatTooltipModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TagModule,
    ButtonModule,
    SkeletonModule
  ]
})
export class ProductsListComponent {
  private productService = inject(ProductService);
  private fb = inject(FormBuilder);
  private router = inject(Router);

  selectedProduct = signal<ProductDTO | null>(null);
  isDetailsOpen = signal(false);
  deleteModalOpen = signal(false);
  deleteModalShown = signal(false);
  productToDelete = signal<ProductDTO | null>(null);
  deleteLoading = signal(false);

  products = signal<ProductDTO[]>([]);
  loading = signal(false);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  detailsLoading = signal(false);
  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(0);
  pageSizeOptions = [20, 50, 100];
  sortField = signal('name');
  sortOrder = signal(1);

  skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];

  private readonly allFilterValue = 'ALL';

  filtersForm = this.fb.group({
    q: [''],
    state: [EntityState.ACTIVE]
  });

  hasActiveFilters = signal(false);

  stateFilterOptions = [
    { value: this.allFilterValue, label: 'Todos' },
    { value: EntityState.ACTIVE, label: 'Activo' },
    { value: EntityState.INACTIVE, label: 'Inactivo' }
  ];

  constructor() {
    this.filtersForm.controls.q.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.filtersForm.controls.state.valueChanges.pipe(
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.loadProducts();
  }

  loadProducts(): void {
    this.loading.set(true);
    this.error.set(null);

    const spec = this.buildSpecFromFilters();
    const direction = this.sortOrder() === 1 ? 'asc' : 'desc';
    const pageable = {
      page: this.currentPage(),
      size: this.pageSize(),
      sort: [`${this.sortField()},${direction}`]
    };

    this.productService.getProducts(spec, pageable).subscribe({
      next: (response) => {
        if (response.ok) {
          this.products.set(response.body);
          if (response.page) {
            this.totalItems.set(response.page.totalItems);
            this.totalPages.set(response.page.totalPages);
            this.pageSize.set(response.page.pageSize);
            this.currentPage.set(Math.max(0, response.page.currentPage - 1));
          } else {
            this.totalItems.set(response.body.length);
            this.totalPages.set(this.getTotalPages());
          }
        } else {
          this.error.set(response.message || 'Error al cargar productos');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: (err) => {
        this.error.set('Error al conectar con el servidor. Verifica que el API esté ejecutándose.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
        console.error('Error loading products:', err);
      }
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const size = event.rows || this.pageSize();
    const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    const nextSortField = rawSortField || this.sortField();
    const nextSortOrder = event.sortOrder ?? this.sortOrder();
    const sortChanged =
      nextSortField !== this.sortField() || nextSortOrder !== this.sortOrder();
    const page = sortChanged
      ? 0
      : event.rows
        ? Math.floor((event.first ?? 0) / event.rows)
        : 0;

    if (!sortChanged && page === this.currentPage() && size === this.pageSize()) return;

    this.sortField.set(nextSortField);
    this.sortOrder.set(nextSortOrder);
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.loadProducts();
  }

  openProductDetails(product: ProductDTO): void {
    if (!product?.id) return;
    this.isDetailsOpen.set(true);
    this.selectedProduct.set(null);
    this.detailsLoading.set(true);
    this.productService.getProductById(product.id).subscribe({
      next: (response) => {
        if (response.ok) {
          this.selectedProduct.set(response.body);
        } else {
          this.error.set(response.message || 'Error al cargar el producto');
        }
        this.detailsLoading.set(false);
      },
      error: (err) => {
        this.error.set('Error al cargar el producto. Verifica que el API esté ejecutándose.');
        this.detailsLoading.set(false);
        console.error('Error loading product by id:', err);
      }
    });
  }

  closeDetails(): void {
    this.isDetailsOpen.set(false);
    this.selectedProduct.set(null);
    this.detailsLoading.set(false);
  }

  onDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeDetails();
  }

  editFromModal(): void {
    const product = this.selectedProduct();
    if (!product?.id) return;
    this.closeDetails();
    this.router.navigate(['/products', product.id]);
  }

  editProduct(product: ProductDTO, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!product?.id) return;
    this.router.navigate(['/products', product.id]);
  }

  openDeleteModal(product: ProductDTO, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!product?.id) return;
    this.productToDelete.set(product);
    this.deleteModalOpen.set(true);
    setTimeout(() => this.deleteModalShown.set(true), 10);
  }

  closeDeleteModal(): void {
    this.deleteModalShown.set(false);
    setTimeout(() => {
      this.deleteModalOpen.set(false);
      this.productToDelete.set(null);
    }, 200);
  }

  confirmDeleteProduct(): void {
    const product = this.productToDelete();
    if (!product?.id) return;

    this.deleteLoading.set(true);
    this.productService.deleteProduct(product.id).subscribe({
      next: (response) => {
        if (response.ok) {
          this.loadProducts();
          this.closeDeleteModal();
        } else {
          this.error.set(response.message || 'Error al eliminar el producto');
        }
        this.deleteLoading.set(false);
      },
      error: (err) => {
        this.error.set(err.error?.message || 'Error al eliminar el producto.');
        this.deleteLoading.set(false);
      }
    });
  }

  applyFilters(): void {
    this.syncActiveFilters();
    this.currentPage.set(0);
    this.loadProducts();
  }

  clearFilters(): void {
    this.filtersForm.reset(
      { q: '', state: EntityState.ACTIVE },
      { emitEvent: false }
    );
    this.applyFilters();
  }

  private syncActiveFilters(): void {
    const raw = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(!!(raw.q?.trim() || raw.state !== EntityState.ACTIVE));
  }

  private isSpecificFilter(value: string | null | undefined): boolean {
    return !!value && value !== this.allFilterValue;
  }

  getStateSeverity(state: string): 'success' | 'danger' | null {
    if (state === 'ACTIVE') return 'success';
    if (state === 'INACTIVE') return 'danger';
    return null;
  }

  private buildSpecFromFilters(): ProductFilterSpec {
    const value = this.filtersForm.getRawValue();
    const spec: ProductFilterSpec = {};
    const q = value.q?.trim();

    if (q) {
      spec['q[lk]'] = q;
    }
    if (this.isSpecificFilter(value.state)) {
      spec.state = value.state;
    } else {
      spec.state = [EntityState.ACTIVE, EntityState.INACTIVE];
    }
    return spec;
  }

  getTotalPages(): number {
    const total = this.totalPages();
    if (total > 0) return total;
    return Math.ceil(this.totalItems() / this.pageSize()) || 1;
  }
}
